import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Form, Input, InputNumber, Button, Popconfirm, message, Select, Switch } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import Title from 'antd/es/typography/Title';
import { getPointsByExploitation, createPoint, updatePoint, deletePoint } from '../../Utils/exploitations/points';
import { getValvesByExploitation } from '../../Utils/exploitations/valves';
import Loading from '../Shared/Loading';
import { AlertCircle } from 'lucide-react';

const { Option } = Select;

const MapComponent = React.memo(({
    center,
    zoom,
    markerPosition,
    onMapClick,
    onZoomChanged,
    onCenterChanged,
    onMarkerDrag
}) => {
    const mapCenter = useMemo(() => ({
        lat: center?.lat ? Number(center.lat) : 0,
        lng: center?.lng ? Number(center.lng) : 0
    }), [center]);

    const mapMarkerPosition = useMemo(() =>
        markerPosition ? {
            lat: Number(markerPosition.lat),
            lng: Number(markerPosition.lng)
        } : null
        , [markerPosition]);

    return (
        <Map
            center={mapCenter}
            zoom={zoom || 15}
            mapTypeId="hybrid"
            style={{ height: '300px', width: '100%' }}
            onClick={onMapClick}
            onZoomChanged={e => onZoomChanged?.(e.detail.zoom)}
            onCenterChanged={e => onCenterChanged?.({
                lat: e.detail.center.lat,
                lng: e.detail.center.lng
            })}
            gestureHandling={'greedy'}
            disableDefaultUI={true}
            zoomControl={true}
        >
            {mapMarkerPosition && (
                <Marker
                    position={mapMarkerPosition}
                    draggable={true}
                    onDragEnd={onMarkerDrag}
                />
            )}
        </Map>
    );
});

const PointForm = React.memo(({ initialValues, onSubmit, onDelete, index, valves }) => {
    const [form] = Form.useForm();
    const [mapCenter, setMapCenter] = useState(() => ({ lat: 46.227638, lng: 2.213749 }));
    const [markerPosition, setMarkerPosition] = useState(null);
    const [zoom, setZoom] = useState(15);
    const [useDropdown, setUseDropdown] = useState(null);

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue(initialValues);

            if (initialValues.valve_id) {
                setUseDropdown(true);
                form.setFieldsValue({ valve: initialValues.valve_id });
            } else {
                setUseDropdown(false);
                form.setFieldsValue({ valve: initialValues.valve_name });
            }

            if (initialValues.coordinates) {
                try {
                    let [initialLat, initialLng] = initialValues.coordinates?.split(',');
                    initialLat = initialLat.replace('°', '');
                    initialLng = initialLng.replace('°', '');
                    
                    initialLat = parseFloat(initialLat);
                    initialLng = parseFloat(initialLng);

                    if (!isNaN(initialLat) && !isNaN(initialLng)) {
                        const newPos = { lat: initialLat, lng: initialLng };
                        setMapCenter(newPos);
                        setMarkerPosition(newPos);
                        form.setFieldsValue({
                            latitude: initialLat.toFixed(6),
                            longitude: initialLng.toFixed(6)
                        });
                    }
                } catch (error) {
                    console.error('Error parsing coordinates:', error);
                }
            }
        }
    }, [form, initialValues]);

    const handleMapClick = useCallback((event) => {
        if (event.detail?.latLng) {
            const newLat = event.detail.latLng.lat;
            const newLng = event.detail.latLng.lng;
            const newPos = { lat: newLat, lng: newLng };
            setMarkerPosition(newPos);
            form.setFieldsValue({
                latitude: newLat.toFixed(6),
                longitude: newLng.toFixed(6)
            });
        }
    }, [form]);

    const handleCoordinateChange = useCallback(() => {
        const latitude = parseFloat(form.getFieldValue('latitude'));
        const longitude = parseFloat(form.getFieldValue('longitude'));
        if (!isNaN(latitude) && !isNaN(longitude)) {
            const newPos = { lat: latitude, lng: longitude };
            setMarkerPosition(newPos);
            setMapCenter(newPos);
        }
    }, [form]);

    const handleMarkerDrag = useCallback((event) => {
        if (event?.latLng) {
            const newLat = event.latLng.lat();
            const newLng = event.latLng.lng();
            const newPos = { lat: newLat, lng: newLng };
            setMarkerPosition(newPos);
            form.setFieldsValue({
                latitude: newLat.toFixed(6),
                longitude: newLng.toFixed(6)
            });
        }
    }, [form]);

    const handleSubmit = useCallback(async (values) => {
        const { latitude, longitude, ...rest } = values;
        if (latitude && longitude) {
            const coordinatesString = `${latitude},${longitude}`;
            try {
                const result = await onSubmit({ ...rest, coordinates: coordinatesString, point_id: initialValues?.point_id }, index);
                    if (result?.location_validation?.is_in_exploitation_area === false) {
                        message.warning('Le point est en dehors de la zone d\'exploitation');
                    }
                    if (result?.location_validation?.is_in_valve_area === false) {
                        message.warning('Le point n\'est pas dans une zone de vanne');
                    }
            } catch (error) {
                // Error handling managed by parent component
            }
        } else {
            message.error('Veuillez entrer les coordonnées ou placer un marqueur sur la carte');
        }
    }, [onSubmit, initialValues?.point_id, index, form]);

    return (
        <Card
            title={index !== undefined ? `Point ${index + 1}` : 'Nouveau point'}
            extra={index !== undefined && (
                <Popconfirm
                    title="Êtes-vous sûr de vouloir supprimer ce point ?"
                    onConfirm={() => onDelete(initialValues.point_id)}
                    okText="Oui"
                    cancelText="Non"
                >
                    <DeleteOutlined className="text-red-500 text-2xl hover:bg-red-100 p-2 rounded-full" />
                </Popconfirm>
            )}
            style={{ marginBottom: 16, width: 300 }}
            className="bg-indigo-50"
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    name="latitude"
                    label="Latitude"
                    rules={[{ required: true, message: 'Veuillez entrer la latitude' }]}
                >
                    <Input onChange={handleCoordinateChange} placeholder="Entrez la latitude" />
                </Form.Item>
                <Form.Item
                    name="longitude"
                    label="Longitude"
                    rules={[{ required: true, message: 'Veuillez entrer la longitude' }]}
                >
                    <Input onChange={handleCoordinateChange} placeholder="Entrez la longitude" />
                </Form.Item>

                <div className="mb-4">
                    <MapComponent
                        center={mapCenter}
                        zoom={zoom}
                        markerPosition={markerPosition}
                        onMapClick={handleMapClick}
                        onZoomChanged={setZoom}
                        onCenterChanged={setMapCenter}
                        onMarkerDrag={handleMarkerDrag}
                    />
                </div>

                <Form.Item
                    label={
                        <span>
                            Vanne:
                            <Switch
                                checked={useDropdown}
                                onChange={setUseDropdown}
                                checkedChildren={<p>Liste</p>}
                                unCheckedChildren={<p>manuelle</p>}
                                style={{ marginLeft: '10px' }}
                            />
                        </span>
                    }
                    rules={[{ required: true, message: 'Veuillez sélectionner une vanne' }]}
                >
                    {useDropdown ? (
                        <Form.Item name="valve_id" noStyle>
                            <Select placeholder="Sélectionnez une vanne">
                                {valves.map((vanne) => (
                                    <Option key={vanne.valve_id} value={vanne.valve_id}>
                                        {vanne.name || `Vanne ${vanne.number}`}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    ) : (
                        <Form.Item name="valve_name" noStyle>
                            <Input placeholder="Saisir le nom de la vanne" />
                        </Form.Item>
                    )}
                </Form.Item>

                <Form.Item name="level1_depth" label="Profondeur niveau 1" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item name="level2_depth" label="Profondeur niveau 2" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item name="hcc_percent" label="HCC %">
                    <Input />
                </Form.Item>
                <Form.Item name="hpfp_percent" label="HPFP %">
                    <Input />
                </Form.Item>
                <Form.Item name="point_number" label="Numéro du point" rules={[{ required: true }]}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        {index !== undefined ? 'Mettre à jour' : 'Créer'}
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
});

const PointsParams = ({ exploitationId }) => {
    const [points, setPoints] = useState([]);
    const [valves, setValves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [locationWarnings, setLocationWarnings] = useState({});

    const MAX_POINTS = 8;

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [pointsData, valvesData] = await Promise.all([
                getPointsByExploitation(exploitationId),
                getValvesByExploitation(exploitationId)
            ]);
            setPoints(Array.isArray(pointsData.data) ? pointsData.data : []);
            setValves(Array.isArray(valvesData) ? valvesData : []);
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error('Erreur lors de la récupération des données');
            setPoints([]);
            setValves([]);
        } finally {
            setLoading(false);
        }
    }, [exploitationId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePointSubmit = useCallback(async (point, index) => {
        try {
            if (index !== undefined) {
                const response = await updatePoint(point.point_id, point).then(res => res.data);

                // Use optional chaining to safely access nested properties.
                // Default to 'false' for isOutside... if location_validation or its properties are missing,
                // meaning no warning will be shown by default for missing data.
                setLocationWarnings(prev => ({
                    ...prev,
                    [point.point_number]: {
                        isOutsideExploitation: !(response?.location_validation?.is_in_exploitation_area ?? true),
                        isOutsideValve: !(response?.location_validation?.is_in_valve_area ?? true),
                        matchedValveId: response?.location_validation?.matched_valve_id,
                        auditChanges: response?.audit_changes // Assuming audit_changes is directly on response
                    }
                }));

                // Check conditions safely using optional chaining.
                // If response or location_validation is null/undefined, the condition will be falsy.
                if (response?.location_validation?.is_in_exploitation_area &&
                    response?.location_validation?.is_in_valve_area) {
                    message.success('Point mis à jour avec succès');
                } else if (response) {
                    // This case is hit if 'response' exists but the full success condition above was not met.
                    // This includes scenarios where location_validation data was missing or validation checks failed.
                    message.warning('Point mis à jour avec des avertissements (par exemple, en dehors de la zone d\'exploitation ou de la vanne)');
                } else {
                    // This case handles if 'response' (i.e., res.data) itself was null or undefined.
                    message.error('La mise à jour du point a échoué ou la réponse du serveur est invalide.');
                }
                return response;
            } else {
                await createPoint({ ...point, exploitation_id: exploitationId });
                message.success('Point créé avec succès');
            }
            await fetchData();
        } catch (error) {
            if (error.response?.status === 400) {
                message.error('La mise à jour a échoué. Veuillez vérifier les données du point', 5);
            } else if (error.response?.status === 430) {
                message.error('Le point n\'est pas dans une vanne');
            } else {
                message.error('Erreur lors de l\'opération sur le point');
            }
            throw error;
        }
    }, [exploitationId, fetchData]);

    const handlePointDelete = useCallback(async (id) => {
        try {
            await deletePoint(id);
            message.success('Point supprimé avec succès');
            await fetchData();
        } catch (error) {
            message.error('Erreur lors de la suppression du point');
        }
    }, [fetchData]);

    if (loading) return <Loading />;

    return (
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
            <div>
                <Title level={3}>Paramètres des points</Title>
                <div className='flex justify-between'>
                    <Title level={4}>Points</Title>
                    {points.length < MAX_POINTS && (
                        <Button
                            type="dashed"
                            onClick={() => setPoints([...points, {}])}
                            style={{ backgroundColor: "#45f", color: "white", marginBottom: 16 }}
                        >
                            <PlusOutlined /> Ajouter un point
                        </Button>
                    )}
                </div>
                
                {Object.entries(locationWarnings).map(([pointNumber, warnings]) => (
                    warnings.isOutsideValve && (
                        <div key={pointNumber} className="flex items-start gap-2 p-3 mb-4 bg-blue-50 rounded-md border border-blue-200">
                            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <div className="space-y-2">
                                <p className="text-blue-700 text-sm font-medium">
                                    Point {pointNumber}: Attention! Le point n'est pas dans une vanne.
                                </p>
                                {warnings.auditChanges?.coordinates && (
                                    <p className="text-blue-700 text-xs">
                                        Changement de coordonnées: {warnings.auditChanges.coordinates.old} → {warnings.auditChanges.coordinates.new}
                                    </p>
                                )}
                            </div>
                        </div>
                    )
                ))}

                {Object.entries(locationWarnings).map(([pointNumber, warnings]) => (
                    warnings.isOutsideExploitation && (
                        <div key={pointNumber} className="flex items-start gap-2 p-3 mb-4 bg-red-50 rounded-md border border-red-200">
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <div className="space-y-2">
                                <p className="text-red-700 text-sm font-medium">
                                    Point {pointNumber}: Attention! Le point est en dehors de la zone d'exploitation.
                                </p>
                                {warnings.auditChanges?.coordinates && (
                                    <p className="text-red-700 text-xs">
                                        Changement de coordonnées: {warnings.auditChanges.coordinates.old} → {warnings.auditChanges.coordinates.new}
                                    </p>
                                )}
                            </div>
                        </div>
                    )
                ))}

                <div className='flex flex-wrap gap-4 w-full'>
                    {points && points.length > 0 ? (
                        points.map((point, index) => (
                            <PointForm
                                key={point.point_id || index}
                                initialValues={point}
                                onSubmit={handlePointSubmit}
                                onDelete={handlePointDelete}
                                index={point.point_id ? index : undefined}
                                valves={valves}
                            />
                        ))
                    ) : (
                        <p>Aucun point disponible pour cette exploitation.</p>
                    )}
                </div>
            </div>
        </APIProvider>
    );
};

export default PointsParams;